/**
 * Admin → Privacy tab. Two panels:
 *   1. Pending 30-day deletions with a Cancel button (support-recovery).
 *   2. Searchable audit trail of every export / delete / cancel action —
 *      the answer to a regulator's "prove you honoured X's request" query.
 *
 * Kept dense so a support agent can scan the whole state in one screen.
 * All destructive actions gated behind an AlertDialog.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ArrowLeftCircle, Download, FileDown, Loader2, Search, ShieldCheck, Trash2, Undo2, X } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const ACTION_META = {
  export:                    { label: 'Data export', icon: Download,      style: 'bg-blue-500/20 text-blue-400' },
  delete_request:            { label: 'Delete requested', icon: Trash2,   style: 'bg-red-500/20 text-red-400' },
  delete_cancelled_by_admin: { label: 'Deletion cancelled', icon: Undo2, style: 'bg-lime/20 text-lime' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
}

function fmtHoursRemaining(h) {
  if (h == null) return '—';
  if (h < 24) return `${Math.max(0, Math.round(h))}h left`;
  return `${Math.round(h / 24)}d left`;
}

// -------------------------------------------------------------------------
// Pending deletions panel
// -------------------------------------------------------------------------
function PendingDeletionsPanel({ onCancelled }) {
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState('');
  const [confirmRow, setConfirmRow] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.adminListPendingDeletions();
      setRows(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load pending deletions.');
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmCancel = async () => {
    if (!confirmRow) return;
    setBusyId(confirmRow.user_id);
    try {
      await api.adminCancelPendingDeletion(confirmRow.user_id);
      toast.success('Account restored.');
      setConfirmRow(null);
      await load();
      onCancelled?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel deletion.');
    } finally {
      setBusyId('');
    }
  };

  return (
    <>
      <div className="rounded-xl border border-[#222] bg-[#0e0e0e] p-5" data-testid="admin-pending-deletions-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-white text-lg flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            Pending Deletions
          </h3>
          <span className="text-xs text-[#666]" data-testid="admin-pending-deletions-count">
            {rows == null ? '' : `${rows.length} in flight`}
          </span>
        </div>
        {rows == null ? (
          <div className="text-[#666] text-sm py-6 text-center">
            <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> loading…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-[#666] text-sm py-6 text-center" data-testid="admin-pending-deletions-empty">
            No accounts pending deletion. Nothing to recover.
          </p>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div
                key={r.user_id}
                className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg bg-[#111] border border-[#222]"
                data-testid={`admin-pending-row-${r.user_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-white truncate">
                      {r.original_name || r.email || r.user_id}
                    </span>
                    <Badge className="text-[10px] bg-[#222] text-[#aaa] capitalize">{r.role || 'user'}</Badge>
                  </div>
                  <p className="text-[11px] text-[#666] truncate">
                    {r.email} · requested {fmtDate(r.requested_at)}
                  </p>
                </div>
                <div className="text-[11px] text-red-300 sm:w-32 shrink-0">
                  Purges {fmtDate(r.purge_at).split(',')[0]}
                  <div className="text-[10px] text-[#666]">{fmtHoursRemaining(r.hours_remaining)}</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-lime/40 text-lime hover:bg-lime hover:text-black shrink-0"
                  onClick={() => setConfirmRow(r)}
                  disabled={busyId === r.user_id}
                  data-testid={`admin-cancel-deletion-${r.user_id}`}
                >
                  <ArrowLeftCircle className="w-3.5 h-3.5 mr-1.5" />
                  Cancel Deletion
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!confirmRow} onOpenChange={(v) => !busyId && !v && setConfirmRow(null)}>
        <AlertDialogContent
          className="bg-surface border-[#262626]"
          data-testid="admin-cancel-deletion-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Restore this account?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              The account for{' '}
              <span className="text-white">{confirmRow?.original_name || confirmRow?.email}</span>{' '}
              will be restored to its state before the delete request. The user
              can log back in immediately. This action is written to the audit
              log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-[#333] bg-transparent text-white hover:bg-[#1a1a1a]"
              disabled={!!busyId}
            >
              Keep pending
            </AlertDialogCancel>
            <AlertDialogAction
              className="btn-primary"
              disabled={!!busyId}
              onClick={(e) => { e.preventDefault(); confirmCancel(); }}
              data-testid="admin-cancel-deletion-confirm-btn"
            >
              {busyId ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Restore account'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// -------------------------------------------------------------------------
// Privacy audit log panel
// -------------------------------------------------------------------------
const ACTION_FILTERS = [
  { value: '',                          label: 'All actions' },
  { value: 'export',                    label: 'Exports' },
  { value: 'delete_request',            label: 'Deletion requests' },
  { value: 'delete_cancelled_by_admin', label: 'Cancellations' },
];
const PAGE_SIZE = 50;

function PrivacyLogPanel({ refreshKey }) {
  const [q, setQ] = useState('');
  const [action, setAction] = useState('');
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.adminListPrivacyLog({
        q: q.trim(), action, limit: PAGE_SIZE, skip,
      });
      setData(payload);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load privacy log.');
      setData({ total: 0, items: [] });
    } finally {
      setLoading(false);
    }
  }, [q, action, skip]);

  // Debounce the search box — no need to hammer the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load, refreshKey]);

  // Streams the current view (respecting q + action, ignoring pagination) as
  // a CSV file — the "email this to your auditor" button. Uses the same
  // params as the list endpoint so what you see is what you download.
  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.adminExportPrivacyLogCsv({ q: q.trim(), action });
      const cd = res.headers?.['content-disposition'] || '';
      const match = /filename="([^"]+)"/.exec(cd);
      const filename = match ? match[1] : `tune4tune-privacy-log-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Privacy log CSV downloaded.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not export CSV.');
    } finally {
      setDownloading(false);
    }
  };

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const hasPrev = skip > 0;
  const hasNext = skip + PAGE_SIZE < total;

  return (
    <div className="rounded-xl border border-[#222] bg-[#0e0e0e] p-5" data-testid="admin-privacy-log-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-white text-lg flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-lime" />
          Privacy Log
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#666]" data-testid="admin-privacy-log-total">
            {total} record{total === 1 ? '' : 's'}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="border-[#333] h-7 text-xs"
            onClick={downloadCsv}
            disabled={downloading || total === 0}
            title="Download the current view as CSV (respects search + filter)"
            data-testid="admin-privacy-log-csv-btn"
          >
            {downloading
              ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : <FileDown className="w-3.5 h-3.5 mr-1.5" />}
            CSV
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
          <Input
            value={q}
            onChange={(e) => { setSkip(0); setQ(e.target.value); }}
            placeholder="Search by email or user id…"
            className="pl-9 bg-black border-[#333] text-white"
            data-testid="admin-privacy-log-search"
          />
          {q && (
            <button
              type="button"
              onClick={() => { setSkip(0); setQ(''); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#666] hover:text-white"
              aria-label="Clear search"
              data-testid="admin-privacy-log-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <select
          value={action}
          onChange={(e) => { setSkip(0); setAction(e.target.value); }}
          className="bg-black border border-[#333] text-white rounded-md px-3 py-2 text-sm"
          data-testid="admin-privacy-log-action-filter"
        >
          {ACTION_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-[#666] text-sm py-6 text-center">
          <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> loading…
        </div>
      ) : items.length === 0 ? (
        <p className="text-[#666] text-sm py-6 text-center" data-testid="admin-privacy-log-empty">
          No records match this search.
        </p>
      ) : (
        <div className="space-y-1.5">
          {items.map((row, i) => {
            const meta = ACTION_META[row.action] || { label: row.action, icon: ShieldCheck, style: 'bg-[#222] text-[#aaa]' };
            const Icon = meta.icon;
            return (
              <div
                key={`${row.user_id}-${row.at}-${i}`}
                className="flex items-center gap-3 p-2.5 rounded bg-[#111] border border-[#222]"
                data-testid={`admin-privacy-log-row-${i}`}
              >
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] shrink-0 ${meta.style}`}>
                  <Icon className="w-3 h-3" /> {meta.label}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white truncate">
                    {row.user_email || row.user_id}
                  </p>
                  {row.admin_email && (
                    <p className="text-[10px] text-[#666] truncate">by admin {row.admin_email}</p>
                  )}
                </div>
                <span className="text-[10px] text-[#666] shrink-0 font-mono">{fmtDate(row.at)}</span>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between mt-4 text-xs text-[#666]">
        <span>
          {items.length === 0
            ? '0'
            : `${skip + 1}–${skip + items.length}`} of {total}
        </span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="border-[#333] h-7"
            onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
            disabled={!hasPrev || loading}
            data-testid="admin-privacy-log-prev"
          >
            Prev
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-[#333] h-7"
            onClick={() => setSkip(skip + PAGE_SIZE)}
            disabled={!hasNext || loading}
            data-testid="admin-privacy-log-next"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Tab shell
// -------------------------------------------------------------------------
export function PrivacyTab() {
  // Bump this after a cancel so the audit log re-fetches to show the fresh
  // `delete_cancelled_by_admin` row without waiting for the debounce.
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <div className="space-y-6" data-testid="admin-privacy-tab">
      <PendingDeletionsPanel onCancelled={() => setRefreshKey((k) => k + 1)} />
      <PrivacyLogPanel refreshKey={refreshKey} />
    </div>
  );
}
